import React from 'react';
import { useLocation } from 'react-router';
import { Card, VStack } from '@adc/parallax-component-library';
import DynamicForm, {
  DynamicFormConfig,
  DynamicFormFieldType,
} from '../../form/DynamicForm';
import { Account, UpdateRequest } from '../../../models';
import { useNavigateWithBreadcrumbs } from '../../../hooks';
import i18n from '../../../utilities/i18n';
import * as Yup from 'yup';
import {
  UserStatus as AccountStatus,
  EntityState,
} from 'adc-sirius-module-common';
import { enumToChoiceOptions } from '../../../utilities/helpers';
import { useMutation } from '@tanstack/react-query';
import { updateAccountMutateOptions } from '../../../queryOptions/account';

type AccountHeaderProps = {
  account: Account;
};

type FormValues = Account & UpdateRequest;

export default function AccountHeader({ account }: AccountHeaderProps) {
  const location = useLocation();
  const navigateWithBreadcrumbs = useNavigateWithBreadcrumbs();

  const updateBreadcrumb = (label) => {
    navigateWithBreadcrumbs(location.pathname, label, label, {
      replace: true,
    });
  };

  const updateAccount = useMutation(updateAccountMutateOptions());

  const formConfig: DynamicFormConfig<FormValues> = {
    fields: [
      {
        name: 'firstName',
        label: i18n.t('Account.firstName'),
        type: DynamicFormFieldType.TEXT,
        isEditable: true,
      },
      {
        name: 'lastName',
        label: i18n.t('Account.lastName'),
        type: DynamicFormFieldType.TEXT,
        isEditable: true,
      },
      {
        name: 'email',
        label: i18n.t('Account.email'),
        type: DynamicFormFieldType.TEXT,
        isEditable: true,
      },
      {
        name: 'username',
        label: i18n.t('Account.username'),
        type: DynamicFormFieldType.TEXT,
        isEditable: false,
      },
      {
        name: 'status',
        label: i18n.t('Account.status'),
        type: DynamicFormFieldType.CHOICE,
        choiceOptions: enumToChoiceOptions(
          AccountStatus,
          'Account.accountStatus',
        ),
      },
      {
        name: 'newUpdateReason',
        label: i18n.t('Page_Site.Form.newUpdateReason'),
        type: DynamicFormFieldType.TEXT,
        isEditable: true,
        isEditOnly: true,
      },
      {
        name: 'createdBy',
        label: i18n.t('Page_Site.Form.createdBy'),
        type: DynamicFormFieldType.TEXT,
      },
      {
        name: 'createdOn',
        label: i18n.t('Page_Site.Form.createdOn'),
        type: DynamicFormFieldType.DATE,
      },
      {
        name: 'updatedBy',
        label: i18n.t('Page_Site.Form.updatedBy'),
        type: DynamicFormFieldType.TEXT,
      },
      {
        name: 'updatedOn',
        label: i18n.t('Page_Site.Form.updatedOn'),
        type: DynamicFormFieldType.DATE,
      },
      {
        name: 'updateReason',
        label: i18n.t('Page_Site.Form.updateReason'),
        type: DynamicFormFieldType.TEXT,
      },
    ],
    validationSchema: {
      firstName: Yup.string().required(i18n.t('Form.genericRequired')),
      lastName: Yup.string().required(i18n.t('Form.genericRequired')),
      email: Yup.string()
        .required(i18n.t('Form.genericRequired'))
        .email(i18n.t('Form.emailInvalid')),
      status: Yup.mixed().oneOf(Object.values(AccountStatus)),
      key: Yup.string(),
      country: Yup.string(),
      username: Yup.string(),
      createdBy: Yup.string(),
      createdOn: Yup.string(),
      updatedBy: Yup.string(),
      updatedOn: Yup.string(),
      updateReason: Yup.string(),
      newUpdateReason: Yup.string().required(i18n.t('Form.genericRequired')),
      state: Yup.mixed().oneOf(Object.values(EntityState)),
    },
    initialValues: {
      ...account,
      newUpdateReason: '',
    },
  };

  const handleSubmit = async (values: Partial<FormValues>) =>
    updateAccount
      .mutateAsync({
        account: {
          ...account,
          ...values,
          updateReason: values.newUpdateReason!,
        },
      })
      .then((account) => {
        updateBreadcrumb(`${account.firstName} ${account.lastName}`);
        return { ...account, newUpdateReason: '' };
      })
      .catch((error) => {
        if (error.status === 409) {
          throw new Error(i18n.t('Page_Account.update_error_conflict'));
        } else {
          throw new Error(i18n.t('Page_Account.update_error_generic'));
        }
      });

  return (
    <VStack flexGrow={1} gap={'$4'}>
      <Card>
        <DynamicForm
          id="account-details"
          title={i18n.t('Page_Account.title')}
          formConfig={formConfig}
          onSave={handleSubmit}
          maxRows={Math.ceil(formConfig.fields.length / 2)}
        />
      </Card>
    </VStack>
  );
}
